/**
 * The Beat.
 *
 * Police attention rotates hourly across the twelve districts on a schedule published in full
 * at Era open. There is no `CityDO`: the Beat is not state, it is a PURE FUNCTION OF TIME over
 * an immutable array, so a client fetches the schedule once and computes the Beat locally with
 * zero further requests, and cron never decides anything.
 *
 * GAME-DESIGN.md section 4. DATA-MODEL.md section 1.2.
 */

import { DISTRICT_IDS, type BeatRelation, type BeatState, type DistrictId } from './types.ts'

export const HOUR_MS = 3_600_000

/** Twelve weeks. GAME-DESIGN 10: "Twelve weeks. Four per year." */
export const ERA_WEEKS = 12

/** 12 weeks x 168 hours. The schedule is exactly this long and immutable for the Era. */
export const BEAT_SLOTS_PER_ERA = ERA_WEEKS * 7 * 24

/**
 * THE ERA WEEK. One function, and it replaced three.
 *
 * There were three of these on 1 August 2026 and two lied in their own comments.
 * `boot.ts` was 1-based and clamped to [1,12]; `lib/family.ts` `eraWeekAt` was 0-based and
 * unclamped while its doc comment said "Week 1 is the first seven days"; `do/family.ts`
 * `eraWeekLabel` measured from the FAMILY's founding while its comment claimed "one place decides
 * what week it is". A Sweep gated on the second fires a week later than the banner the player is
 * reading, which is the exact shape of bug the ceremony cannot afford.
 *
 * **It is a twelfth of the Era, not a fixed seven days**, and that is the one substantive change.
 * In production the two are identical, because an Era is exactly 84 days and a twelfth of 84 days
 * IS seven days. They differ only when the Era is not twelve weeks long, and the only place that
 * happens is staging, whose `ERA_ENDS_AT` is 2099-01-01. Under the fixed-seven-days reading
 * staging sits at week 12 forever: a Sweep gated on week 11 is permanently ON there and a Last
 * Call gated on the Era end is permanently OFF, so the two close-ceremony beats disagree about
 * whether the Era is ending. Under this one, staging is at week 1 for the next seventy-three
 * years and every Era-phase gate is consistently off, which is the honest answer for a
 * environment that is not running an Era at all.
 *
 * Clamped at both ends. Before the Era opens is week 1, after it closes is week 12, because a
 * caller that wants the refusal wants `beatIndexAt`, which throws, and a caller that wants a
 * number to print wants a number in range.
 */
export function eraWeek(now: number, startsAt: number, endsAt: number): number {
  const span = endsAt - startsAt
  if (!(span > 0)) return 1
  const through = (now - startsAt) / span
  const week = Math.floor(through * ERA_WEEKS) + 1
  return week < 1 ? 1 : week > ERA_WEEKS ? ERA_WEEKS : week
}

/**
 * The first week of The Sweep.
 *
 * GAME-DESIGN 10.2: "The last two weeks are a finale, not a wind-down." 1j.14 ruled the finale a
 * CONSTANT rather than a variant, published as fixed and identical in every Era, and forbade any
 * Era variant from touching Heat, RICO, the Sweep Legend multiplier or Control regeneration.
 */
export const SWEEP_FIRST_WEEK = 11

/** Whether The Sweep is running at `now`. The only predicate; every effect reads this one. */
export const inSweep = (now: number, startsAt: number, endsAt: number): boolean =>
  eraWeek(now, startsAt, endsAt) >= SWEEP_FIRST_WEEK

/**
 * Thrown when the Beat is asked about an instant that is not inside the Era.
 *
 * A named class rather than a bare `Error` because both the Worker and the SPA catch this, and
 * neither can import the other's error type: `shared/` is the only thing they have in common.
 */
export class OutsideEraError extends Error {
  readonly index: number
  readonly slots: number
  constructor(message: string, index: number, slots: number) {
    super(message)
    this.name = 'OutsideEraError'
    this.index = index
    this.slots = slots
  }
}

/**
 * The slot index at an instant. THROWS outside the Era rather than returning a number.
 *
 * This is the guard that would have caught the Era-start misconfiguration in Phase 4. Before the
 * Era opens the index is negative, and `beatAt`'s modulo wrap turned that into a real district:
 * the rotation ran backwards off the end of the array and every screen looked plausible. A wrong
 * answer that looks right is worse than a crash, so this crashes.
 *
 * `slots` is the schedule length. Pass it and the upper bound is checked too; omit it and only
 * the lower bound is. Callers that have the schedule should always pass it.
 */
export function beatIndexAt(tMs: number, eraStartsAt: number, slots?: number): number {
  const i = Math.floor((tMs - eraStartsAt) / HOUR_MS)
  if (i < 0) {
    throw new OutsideEraError(
      `the Beat was asked about ${new Date(tMs).toISOString()}, which is before the Era opened at ${new Date(eraStartsAt).toISOString()}`,
      i,
      slots ?? BEAT_SLOTS_PER_ERA,
    )
  }
  if (slots !== undefined && i >= slots) {
    throw new OutsideEraError(
      `the Beat was asked about slot ${i}, and the Era is ${slots} hours long. The Era is over.`,
      i,
      slots,
    )
  }
  return i
}

export interface BeatEra {
  startsAt: number
}

/**
 * The district that is Hot at `tMs`, the district that is Cooling (Hot in the previous hour),
 * and the exact boundaries of the slot. Multipliers live in shared/formulas.ts `BEAT`.
 *
 * Throws `OutsideEraError` outside the Era. There is no sensible Beat before an Era opens or
 * after it closes, and inventing one is exactly the bug this guard exists for.
 */
export function beatAt(tMs: number, era: BeatEra, schedule: readonly DistrictId[]): BeatState {
  const i = beatIndexAt(tMs, era.startsAt, schedule.length)
  const startsAt = era.startsAt + i * HOUR_MS
  return {
    district: schedule[i],
    // Slot 0 has no previous hour. Wrapping to the last slot of the Era would make the twelfth
    // week's final district Cooling on opening night, which is nonsense.
    cooling: i > 0 ? schedule[i - 1] : null,
    payout: 0.4,
    heatMult: 2,
    startsAt,
    endsAt: startsAt + HOUR_MS,
    index: i,
  }
}

/** The Beat if there is one, or null outside the Era. For callers that render rather than decide. */
export function beatAtOrNull(
  tMs: number,
  era: BeatEra,
  schedule: readonly DistrictId[],
): BeatState | null {
  try {
    return beatAt(tMs, era, schedule)
  } catch (e) {
    if (e instanceof OutsideEraError) return null
    throw e
  }
}

/** Hot, Cooling or Cold for a given district at a given instant. */
export function beatRelation(district: DistrictId, beat: BeatState): BeatRelation {
  if (district === beat.district) return 'hot'
  if (beat.cooling !== null && district === beat.cooling) return 'cooling'
  return 'cold'
}

/**
 * Build the Era's schedule from a deterministic byte stream.
 *
 * The sequence is NOT a simple 12-hour rotation. A 12-slot cycle repeats at the same clock hour
 * every day, which systematically favours one timezone per district and hands a script a
 * deterministic 24-hour plan. Instead each block of 12 slots is an independent permutation of
 * the twelve districts, with one constraint: the last district of block N may not be the first
 * district of block N+1, so no district is ever Hot for two consecutive hours.
 *
 * Because 12 does not divide 24 evenly against a shuffled permutation, a district's hot hours
 * precess through the day across a week, so no player's waking hours are structurally
 * advantaged. Because the sequence is seeded and published at Era open, a player can plan a
 * fortnight ahead.
 *
 * `bytes` must supply at least `ceil(slots / 12) * 12` values; the caller derives them from
 * HMAC-SHA256(eraSecret, "beat") expanded to the required length.
 */
export function buildBeatSchedule(bytes: Uint8Array, slots = BEAT_SLOTS_PER_ERA): DistrictId[] {
  const districts = [...DISTRICT_IDS]
  const out: DistrictId[] = []
  let cursor = 0
  const next = (): number => {
    const b = bytes[cursor % bytes.length]
    cursor++
    return b
  }

  let previousLast: DistrictId | null = null
  while (out.length < slots) {
    // Fisher-Yates over a copy, driven by the byte stream.
    const block = [...districts]
    for (let i = block.length - 1; i > 0; i--) {
      const j = next() % (i + 1)
      const t = block[i]
      block[i] = block[j]
      block[j] = t
    }
    // Constraint: no district is Hot twice in a row across the block boundary.
    if (previousLast !== null && block[0] === previousLast) {
      const t = block[0]
      block[0] = block[block.length - 1]
      block[block.length - 1] = t
    }
    previousLast = block[block.length - 1]
    for (const d of block) {
      if (out.length >= slots) break
      out.push(d)
    }
  }
  return out
}

/**
 * A schedule is only usable if it never repeats a district across a boundary and covers all
 * twelve. Called by the Era creation script before the schedule is written to KV, and by tests.
 */
export function validateBeatSchedule(schedule: readonly DistrictId[]): string[] {
  const problems: string[] = []
  if (schedule.length !== BEAT_SLOTS_PER_ERA) {
    problems.push(`schedule has ${schedule.length} slots, expected ${BEAT_SLOTS_PER_ERA}`)
  }
  for (let i = 1; i < schedule.length; i++) {
    if (schedule[i] === schedule[i - 1]) {
      problems.push(`district ${schedule[i]} is hot twice in a row at slot ${i}`)
      break
    }
  }
  const seen = new Set(schedule)
  for (const d of DISTRICT_IDS) {
    if (!seen.has(d)) problems.push(`district ${d} never becomes hot`)
  }
  // Every full block of 12 must be a permutation, so counts stay within one of each other.
  const counts = new Map<DistrictId, number>()
  for (const d of schedule) counts.set(d, (counts.get(d) ?? 0) + 1)
  const values = [...counts.values()]
  if (values.length && Math.max(...values) - Math.min(...values) > 1) {
    problems.push(`hot-hour counts are uneven: ${Math.min(...values)} to ${Math.max(...values)}`)
  }
  return problems
}
